var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { convertProcessToNodeMap } from 'sweet-graphs';
import { TaskState } from './task-state';
import { getProcedureTaskCore } from './procedure-task-core';
import { ProcessTaskPayload } from './payload/process-task-payload';
import { processProgressToProgressMap } from '../process-task/process-progress-to-progress-map';
import { extractProcessTaskProgressInfo } from '../process-task/extract-process-task-progress-info';
import { calculatePercentComplete } from '../task-progress/calculate-percent-complete';
import { mergeProgressIntoTask } from './payload/merge-task-progress';
var BaseProcessTaskCore = /** @class */ (function () {
    function BaseProcessTaskCore(task) {
        this.ct = 'process';
        this.progress = null;
        /* fallback function for tasks without version */
        this.calculateProgressUpdated = function (progressPayload) {
            return {
                is_completeable: true,
                progress_percent: null,
            };
        };
        /* payload class */
        this._payload = null;
        this.task = task;
        this.progress = task.progress;
    }
    Object.defineProperty(BaseProcessTaskCore.prototype, "isCompleted", {
        get: function () {
            return this.task.completed != null;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(BaseProcessTaskCore.prototype, "completedBy", {
        get: function () {
            if (this.task.completed_by != null) {
                return this.task.completed_by;
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(BaseProcessTaskCore.prototype, "payload", {
        get: function () {
            if (this._payload == null)
                this._payload = new ProcessTaskPayload(this.calculateProgressUpdated);
            return this._payload;
        },
        enumerable: false,
        configurable: true
    });
    return BaseProcessTaskCore;
}());
var ProcessTaskCoreWithNoVersion = /** @class */ (function (_super) {
    __extends(ProcessTaskCoreWithNoVersion, _super);
    function ProcessTaskCoreWithNoVersion() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.hasVersion = false;
        return _this;
    }
    Object.defineProperty(ProcessTaskCoreWithNoVersion.prototype, "titles", {
        get: function () {
            var titles = [];
            if (this.task.description != null) {
                titles.push(this.task.description);
            }
            titles.push(this.task.name);
            return titles;
        },
        enumerable: false,
        configurable: true
    });
    return ProcessTaskCoreWithNoVersion;
}(BaseProcessTaskCore));
export { ProcessTaskCoreWithNoVersion };
var ProcessTaskCoreWithVersion = /** @class */ (function (_super) {
    __extends(ProcessTaskCoreWithVersion, _super);
    function ProcessTaskCoreWithVersion(task, version) {
        var _this = _super.call(this, task) || this;
        _this.version = version;
        _this.hasVersion = true;
        /* state */
        _this._state = null;
        /**
         * Merge progress payload into current progress object to calculate
         * `progress_percent` and `is_completeable`, so these fields can be added to update
         * payload
         */
        _this.calculateProgressUpdated = function (progressPayload) {
            // force recalculation of state
            var originalState = _this._state;
            var originalProgress = _this.progress;
            var originalPercentComplete = _this._percentComplete;
            _this._state = null;
            _this._percentComplete = null;
            _this.progress = mergeProgressIntoTask(progressPayload, _this.task).progress;
            var progress_percent = _this.percentComplete != null ? Math.round(_this.percentComplete) : null;
            var is_completeable = _this.state.progressInfo.canBeCompleted;
            _this._state = originalState;
            _this.progress = originalProgress;
            _this._percentComplete = originalPercentComplete;
            return { progress_percent: progress_percent, is_completeable: is_completeable };
        };
        return _this;
    }
    Object.defineProperty(ProcessTaskCoreWithVersion.prototype, "titles", {
        get: function () {
            var titles = [];
            if (this.task.description != null) {
                titles.push(this.task.description);
            }
            titles.push(this.version.name);
            return titles;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ProcessTaskCoreWithVersion.prototype, "state", {
        get: function () {
            var _this = this;
            if (this._state == null) {
                var _a = this.version, content_1 = _a.content, steps_blocked_at_1 = _a.steps_blocked_at;
                this._state = new TaskState(steps_blocked_at_1 != null, function () { return convertProcessToNodeMap(content_1); }, function (nodeMap) {
                    return processProgressToProgressMap(_this.progress, nodeMap);
                }, function (nodeMap, progressMap) {
                    return extractProcessTaskProgressInfo(nodeMap, progressMap, steps_blocked_at_1 != null);
                }, this.task.assigned_version_content != null
                    ? this.task.assigned_version_content.lanes
                    : {}, this.task.assignments);
            }
            return this._state;
        },
        enumerable: false,
        configurable: true
    });
    ProcessTaskCoreWithVersion.prototype.calculatePercentComplete = function () {
        var _a = this.state.progressInfo, completedPath = _a.completedPath, activeNodes = _a.activeNodes, farthestEndingPath = _a.farthestEndingPath;
        // if it's a looping process let's just stop here and assume we don't know the %
        if (farthestEndingPath == null) {
            return null;
        }
        // remove active node otherwise it will be counted twice
        farthestEndingPath === null || farthestEndingPath === void 0 ? void 0 : farthestEndingPath.shift();
        activeNodes.delete('artificial-ending');
        var completedProcs = this.getCompletedAndPendingSteps(completedPath, true);
        var activeProcs = this.getCompletedAndPendingSteps(Array.from(activeNodes), false);
        var pendingProcs = this.getCompletedAndPendingSteps(farthestEndingPath, false);
        var completed = completedProcs.completed + completedProcs.pending + activeProcs.completed;
        var pending = activeProcs.pending + pendingProcs.completed + pendingProcs.pending;
        return calculatePercentComplete(completed, pending);
    };
    Object.defineProperty(ProcessTaskCoreWithVersion.prototype, "percentComplete", {
        get: function () {
            if (this._percentComplete == null) {
                this._percentComplete = this.calculatePercentComplete();
            }
            return this._percentComplete;
        },
        enumerable: false,
        configurable: true
    });
    // calculate completed and pending procedure-steps (sub-steps) in a path of procedures/steps
    ProcessTaskCoreWithVersion.prototype.getCompletedAndPendingSteps = function (path, stepIsCompleted) {
        var completed = 0;
        var pending = 0;
        // lets iterate over the completed steps and count them
        // when the step is a procedure let's count all valid steps of that procedure
        for (var i = 0; i < path.length; i++) {
            var stepId = path[i];
            var stepNode = this.state.nodeMap[stepId];
            // the step is content or decision step, we count as a single step then
            if (stepNode.type === 'content' || stepNode.type === 'decision') {
                completed += stepIsCompleted ? 1 : 0;
                pending += stepIsCompleted ? 0 : 1;
                // the step is a procedure, as it's marked as completed, let's
                // sum up the completed + possible longest-path steps on it
            }
            else if (stepNode.type === 'processprocedure') {
                if (stepNode.body.deleted) {
                    continue;
                }
                var procedureTaskCore = getProcedureTaskCore(this.task, stepId);
                if (!procedureTaskCore.hasVersion) {
                    continue;
                }
                var _a = procedureTaskCore.state.progressInfo, completedPath = _a.completedPath, farthestEndingDistance = _a.farthestEndingDistance, activeNodes = _a.activeNodes;
                activeNodes.delete('artificial-ending');
                // a procedure with no steps - let's treat it as a single step so it can have
                // a minimum weight in the total percentage calculation
                if (completedPath.length === 0 &&
                    farthestEndingDistance === 0 &&
                    activeNodes.size === 0) {
                    completed += stepIsCompleted ? 1 : 0;
                    pending += stepIsCompleted ? 0 : 1;
                }
                completed += completedPath.length;
                // subtracts the active-node from farthest ending length
                pending += Math.max(0, farthestEndingDistance - 1) + activeNodes.size;
            }
        }
        return { completed: completed, pending: pending };
    };
    return ProcessTaskCoreWithVersion;
}(BaseProcessTaskCore));
export { ProcessTaskCoreWithVersion };
export function getProcessTaskCore(task) {
    var version = task.assigned_version_content;
    if (task.template.assigned_object.content_type == 'proceduregroup' &&
        (version == null || version.content_type === 'processversion')) {
        return version != null
            ? new ProcessTaskCoreWithVersion(task, version)
            : new ProcessTaskCoreWithNoVersion(task);
    }
    throw Error('ProcessTaskCore initialized with procedure-assigned task: ' + task.url);
}
